import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Fish, Sprout, Droplet, Building2, BrainCircuit, Quote,
} from "lucide-react";
import { fetchCurriculum } from "@/lib/api";
import WeekCard from "@/components/WeekCard";
import LeaderboardWidget from "@/components/LeaderboardWidget";
import { useLang } from "@/contexts/LangContext";

const HERO_IMG =
    "https://images.unsplash.com/photo-1623251963103-511f665a9782?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
    const { lang, t } = useLang();
    const [weeks, setWeeks] = useState([]);
    useEffect(() => {
        fetchCurriculum(lang).then(setWeeks).catch(() => {});
    }, [lang]);

    return (
        <main data-testid="home-page">
            <section className="relative overflow-hidden grid-bg">
                <div className="glow glow-lime -top-32 -left-32" />
                <div className="glow glow-cyan top-20 right-0" />
                <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28 relative z-10">
                    <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                        <div className="col-span-12 lg:col-span-7">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="tag">
                                    <span className="w-2 h-2 rounded-full bg-[var(--lime)] animate-pulse" />
                                    {t.home.tag}
                                </span>
                            </div>
                            <h1
                                className="font-display font-black text-white text-5xl md:text-7xl lg:text-8xl leading-[0.92] uppercase"
                                data-testid="hero-headline"
                            >
                                {t.home.line1}
                                <br />
                                {t.home.line2_a}{" "}
                                <span className="spray text-white">{t.home.line2_b}</span>.
                                <br />
                                <span className="font-brush text-[var(--coral)] text-6xl md:text-8xl lg:text-9xl inline-block -rotate-3 mt-2">
                                    {t.home.line3}
                                </span>
                            </h1>
                            <p className="font-mono text-base md:text-lg text-[var(--muted)] mt-8 max-w-xl leading-relaxed">
                                {t.home.blurb}
                            </p>
                            <div
                                className="flex flex-wrap gap-4 mt-10"
                                data-testid="hero-cta-group"
                            >
                                <Link
                                    to="/curriculum"
                                    className="btn-prime"
                                    data-testid="cta-start-program"
                                >
                                    {t.home.startCta} <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/teach"
                                    className="btn-ghost"
                                    data-testid="cta-teacher-tools"
                                >
                                    {t.home.teacherCta}
                                </Link>
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-5 relative">
                            <motion.div
                                initial={{ rotate: -3, opacity: 0, y: 30 }}
                                animate={{ rotate: -2, opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, ease: "easeOut" }}
                                className="frame"
                            >
                                <img
                                    src={HERO_IMG}
                                    alt="Urban aquaponics greenhouse"
                                    className="w-full h-[360px] md:h-[460px] object-cover"
                                />
                                <span className="sticker -top-4 -right-4">
                                    {t.home.sticker_weeks}
                                </span>
                                <span
                                    className="sticker"
                                    style={{
                                        bottom: -16,
                                        left: -16,
                                        background: "var(--cyan)",
                                        color: "#000",
                                        transform: "rotate(8deg)",
                                    }}
                                >
                                    {t.home.sticker_science}
                                </span>
                            </motion.div>
                        </div>
                    </div>
                </div>

                <div className="border-y border-[var(--border)] bg-black/40 py-3">
                    <div className="marquee">
                        <div className="marquee-inner font-mono text-xs uppercase tracking-[0.28em] text-[var(--cyan)]">
                            {Array.from({ length: 2 }).map((_, k) => (
                                <span key={k} className="flex items-center gap-12">
                                    <span>fish + plants + bacteria</span>
                                    <span className="text-[var(--lime)]">★</span>
                                    <span>food sovereignty</span>
                                    <span className="text-[var(--coral)]">●</span>
                                    <span>nitrogen cycle</span>
                                    <span className="text-[var(--lime)]">★</span>
                                    <span>build your own farm</span>
                                    <span className="text-[var(--coral)]">●</span>
                                    <span>roots in concrete</span>
                                    <span className="text-[var(--lime)]">★</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 relative">
                <div className="glow glow-coral -bottom-40 -right-20" />
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-5">
                        <span className="tag mb-4 inline-flex">{t.home.whyTag}</span>
                        <h2 className="font-display font-black text-white text-4xl md:text-5xl uppercase leading-[0.95]">
                            {t.home.whyHead_a}{" "}
                            <span className="text-[var(--lime)]">
                                {t.home.whyHead_b}
                            </span>{" "}
                            {t.home.whyHead_c}
                        </h2>
                        <p className="font-mono text-[var(--muted)] mt-5 leading-relaxed">
                            {t.home.whyBody}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-7 grid grid-cols-2 gap-4 md:gap-5 relative z-10">
                        {[
                            { Icon: Droplet, stat: "90%", label: "less water", color: "var(--cyan)" },
                            { Icon: Sprout, stat: "8×", label: "more food per sq ft", color: "var(--lime)" },
                            { Icon: Fish, stat: "0", label: "fertilizer runoff", color: "var(--coral)" },
                            { Icon: Building2, stat: "1M+", label: "rooftops · NYC", color: "var(--cyan)" },
                        ].map(({ Icon, stat, label, color }, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06 }}
                                className="card p-5"
                            >
                                <Icon size={20} style={{ color }} />
                                <div
                                    className="font-display font-black text-4xl mt-3"
                                    style={{ color }}
                                >
                                    {stat}
                                </div>
                                <p className="font-mono text-xs text-[var(--muted)] mt-1">
                                    {label}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16"
                data-testid="modules-section"
            >
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <span className="tag mb-3 inline-flex">{t.home.modulesTag}</span>
                        <h2 className="font-display font-black text-white text-3xl md:text-5xl uppercase leading-[0.95]">
                            {t.home.modulesHead_a}{" "}
                            <span className="font-brush text-[var(--cyan)] -rotate-2 inline-block">
                                {t.home.modulesHead_b}
                            </span>
                        </h2>
                    </div>
                    <Link
                        to="/curriculum"
                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)]"
                    >
                        {t.home.modulesSee}
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {weeks.map((w, i) => (
                        <WeekCard key={w.id} week={w} index={i} />
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 md:col-span-6 card p-8 relative overflow-hidden">
                        <span className="absolute -top-12 -right-12 text-[var(--lime)]/10 font-display font-black text-9xl">
                            AI
                        </span>
                        <BrainCircuit size={28} className="text-[var(--lime)]" />
                        <h3 className="font-display font-bold text-white text-2xl mt-4 uppercase">
                            {t.home.aiTutorHead}
                        </h3>
                        <p className="font-mono text-sm text-[var(--muted)] mt-3 leading-relaxed">
                            {t.home.aiTutorBody}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 card p-8 relative overflow-hidden">
                        <span className="absolute -top-12 -right-12 text-[var(--coral)]/10 font-brush text-9xl">
                            plan
                        </span>
                        <Quote size={28} className="text-[var(--coral)]" />
                        <h3 className="font-display font-bold text-white text-2xl mt-4 uppercase">
                            {t.home.aiPlanHead}
                        </h3>
                        <p className="font-mono text-sm text-[var(--muted)] mt-3 leading-relaxed">
                            {t.home.aiPlanBody}
                        </p>
                        <Link
                            to="/teach"
                            className="btn-coral mt-5 inline-flex"
                            data-testid="cta-open-teach"
                        >
                            {t.home.openTeach} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            <LeaderboardWidget />
        </main>
    );
}
